import { Checkbox, Input, Scope, useFormApi } from 'informed';
import { useState } from 'react';
import TymberInfoBox from '../TymberInfoBox';
import moment from 'moment';
import {
  calculateDurationFromWorkTime,
  calculateWorkTime,
} from '../../../../utils/time';
import { PrimaryButton } from '@tymbe/ui-primitives';
import { ContentIcon, FavouriteIcon } from '@tymbe/ui-icons';
import DropDownMenu from '../ShiftDetail/DropDownMenu';
import { AttendanceResolution } from '@tymbe/schema/enums';
import classNames from 'classnames';
import { TyRadioGroup } from '@tymbe/ty-components/inputs/radio';
import { ThumbRadio } from '../ThumbRadio';
import { TyInput, timeFormatter } from '@tymbe/ui-forms';
import {
  BAD_JOB_EVALUATION_SCORE,
  DEFAULT_JOB_EVALUATION_SCORE,
  GOOD_JOB_EVALUATION_SCORE,
} from '../../../../utils/constants';
import { ApplicationData } from '../../../../types/TymbeApi';

interface UnapprovedAttendanceRowProps {
  application: ApplicationData;
  submitSingle: (key: number) => Promise<void>;
  startDate: string;
  endDate: string;
}

const UnapprovedAttendanceRow = ({
  application,
  submitSingle,
  startDate,
  endDate,
}: UnapprovedAttendanceRowProps) => {
  const [openMenu, setOpenMenu] = useState(false);

  const formApi = useFormApi();
  const workedTimeFromStartEnd = (start: string, end: string) => {
    const startMoment = moment(start, 'H:mm');
    const endMoment = moment(end, 'H:mm');

    // If end is before or equal to start, assume it's the next day
    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }
    return moment.duration(endMoment.diff(startMoment)).asHours();
  };

  const initialTotalTime = workedTimeFromStartEnd(
    moment(startDate).format('H:mm'),
    moment(endDate).format('H:mm')
  );
  const initialWorkedTime = calculateWorkTime(
    startDate,
    endDate,
    false,
    'hour'
  );

  const endFromStartAndWorkedTime = (start: string, workedTime: number) => {
    return moment(start, 'H:mm').add(workedTime, 'hours').format('H:mm');
  };

  const handleTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const end = formApi.getValue(`applications.${application.id}.end`);
    const startMoment = moment(start, 'H:mm');
    const endMoment = moment(end, 'H:mm');

    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, 'day');
    }

    if (startMoment && endMoment) {
      const workedTime = calculateWorkTime(
        startMoment,
        endMoment,
        false,
        'hour'
      );
      const totalTime = workedTimeFromStartEnd(start, end);
      const breakTime = totalTime - workedTime;
      const oldWorkedTime = formApi.getValue(`applications.${application.id}.confirmed_time`)

      if (workedTime === oldWorkedTime || formApi.getFocused(`applications.${application.id}.confirmed_time`)) return;

      formApi.setValue(`applications.${application.id}.confirmed_time`, workedTime);
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleWorkedTimeChange = () => {
    const start = formApi.getValue(`applications.${application.id}.start`);
    const workedTime = formApi.getValue(`applications.${application.id}.confirmed_time`);

    if (start && workedTime != null) {
      const totalWorkTime = calculateDurationFromWorkTime(
        workedTime * 3600000,
        false,
        'hours'
      );

      const newEnd = endFromStartAndWorkedTime(start, totalWorkTime);
      const breakTime = totalWorkTime - workedTime;
      const oldEnd =formApi.getValue(`applications.${application.id}.end`);

      if (oldEnd === newEnd || formApi.getFocused(`applications.${application.id}.end`)) return;

      formApi.setValue(`applications.${application.id}.end`, newEnd);
      formApi.setValue(`applications.${application.id}.break`, breakTime);
    }
  };

  const handleAttResolution = (resolution: AttendanceResolution) => {
    formApi.setValue(`applications.${application.id}.resolution`, resolution);
    submitSingle(application.id);
  };

  const menuOptions = [
    {
      text: 'Omluvená absence',
      value: AttendanceResolution.EXCUSED_ABSENCE,
      onClick: () => handleAttResolution(AttendanceResolution.EXCUSED_ABSENCE),
    },
    {
      text: 'Neomluvená absence',
      value: AttendanceResolution.UNEXCUSED_ABSENCE,
      onClick: () =>
        handleAttResolution(AttendanceResolution.UNEXCUSED_ABSENCE),
    },
    {
      text: 'Storno',
      value: AttendanceResolution.CANCEL,
      onClick: () => handleAttResolution(AttendanceResolution.CANCEL),
    },
  ];

  return (
    <Scope scope={`applications.${application.id}`}>
      <div className="grid grid-cols-[25%_auto_25%] gap-4 p-4">
        <div className="flex items-center gap-2">
          <Input
            name="id"
            required
            type="hidden"
            initialValue={application.id}
          />
          <Input
            name="resolution"
            required
            type="hidden"
            initialValue={AttendanceResolution.OK}
          />
          <Checkbox className="size-[18px] rounded" name="selected" />
          <TymberInfoBox personId={application.person_id} />
        </div>

        <div className="flex items-center gap-6">
          <div className="flex gap-6 w-48">
            <TyInput
              className="w-[90px]"
              name="confirmed_time"
              suffix="h"
              onChange={handleWorkedTimeChange}
              initialValue={initialWorkedTime}
              mask={(value) => Number(value).toFixed(2)}
              maskOnBlur
            />
            <TyInput
              className="w-[75px]"
              name="break"
              suffix="h"
              initialValue={initialTotalTime - initialWorkedTime}
              disabled
            />
          </div>
          <div className="flex gap-3 items-center">
            <TyInput
              className="w-16"
              name="start"
              formatter={timeFormatter}
              onChange={handleTimeChange}
              initialValue={moment(startDate).format('H:mm')}
            />
            <span>-</span>
            <TyInput
              className="w-16"
              name="end"
              formatter={timeFormatter}
              onChange={handleTimeChange}
              initialValue={moment(endDate).format('H:mm')}
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <TyRadioGroup
            name="rating"
            defaultValue={`${DEFAULT_JOB_EVALUATION_SCORE}`}
            className="flex items-center gap-3"
          >
            <ThumbRadio variant="down" value={`${BAD_JOB_EVALUATION_SCORE}`} />
            <ThumbRadio variant="up" value={`${GOOD_JOB_EVALUATION_SCORE}`} />
          </TyRadioGroup>
          <PrimaryButton
            className="flex gap-2 items-center h-10"
            onClick={() => {
              submitSingle(application.id);
            }}
          >
            <FavouriteIcon name="check" className="size-[18px]" />
            Potvrdit
          </PrimaryButton>
          <button
            className={classNames(
              'relative rounded-full border border-red-300 size-10 p-0 flex justify-center',
              'items-center text-red-500 disabled:text-secondary-400 disabled:border-secondary-200'
            )}
            onClick={() => setOpenMenu(!openMenu)}
          >
            <ContentIcon name="block" className="size-[18px]" />
            {openMenu && (
              <DropDownMenu
                className="-translate-x-[9.5rem] translate-y-20 z-50"
                options={menuOptions}
                onBlur={() => setOpenMenu(false)}
              />
            )}
          </button>
        </div>
      </div>
    </Scope>
  );
};

export default UnapprovedAttendanceRow;
